import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import { Box, Chip, CircularProgress } from "@mui/material";
import { useSnackbar } from "notistack";
import api from "../../../api/V4client";
import { Flex } from "../../../components/Container/Flex";
import TextButton from "../../../components/Controls/TextButton";
import { Bold, Comment, Text } from "../../../utils/style/texts";
import { Colors } from "../../../utils/style/colors";
import { DEVICES } from "../../../redux/constants/device.constants";
import { updateDevice } from "../../../redux/actions/deviceV4.actions";
import { formatDateTime } from "../../../utils/time";

const HISTORY_LIMIT = 5;

const RESULT_COLORS = {
  succeeded: Colors.success,
  failed: Colors.danger,
  cancelled: Colors.gray40,
};

// result is "" while a run is still in progress.
const statusLabel = (run, t) => {
  if (run.result) return run.result;
  return t("inProgress", { defaultValue: "In progress" });
};

const statusColor = (run) => RESULT_COLORS[run.result] || Colors.warning;

// Only before the device starts applying — aborting past 0% half-writes it.
const isCancellable = (run) => run.result === "" && !run.progress;

const REL_FMT =
  typeof Intl !== "undefined" && Intl.RelativeTimeFormat
    ? new Intl.RelativeTimeFormat(undefined, { numeric: "auto" })
    : null;

const REL_UNITS = [
  ["year", 31536000000],
  ["month", 2592000000],
  ["day", 86400000],
  ["hour", 3600000],
  ["minute", 60000],
  ["second", 1000],
];

// "2 hours ago" label; "" when unavailable so callers fall back to the date.
const relativeTime = (iso) => {
  if (!iso || !REL_FMT) return "";
  const diff = new Date(iso).getTime() - Date.now();
  if (Number.isNaN(diff)) return "";
  for (const [unit, ms] of REL_UNITS) {
    if (Math.abs(diff) >= ms || unit === "second") {
      return REL_FMT.format(Math.round(diff / ms), unit);
    }
  }
  return "";
};

// Pretty-print the diff string as JSON when possible, else show it verbatim.
const formatDiff = (diff) => {
  if (!diff) return null;
  try {
    return JSON.stringify(JSON.parse(diff), null, 2);
  } catch (_) {
    return String(diff);
  }
};

const MIN_VALID_TS = new Date("2000-01-01T00:00:00Z").getTime();

// Treat zero-value (e.g. "0001-01-01") and unparseable dates as absent.
const validDate = (iso) => {
  if (!iso) return null;
  const t = new Date(iso).getTime();
  return Number.isNaN(t) || t < MIN_VALID_TS ? null : iso;
};

function SyncRunItem({ run, t, adminMode, onCancel, cancelling }) {
  const diff = formatDiff(run.diff);
  const ts =
    validDate(run.completed_at) ||
    validDate(run.updated_at) ||
    validDate(run.created_at);

  return (
    <Box sx={{ py: 1.5, borderBottom: `1px solid ${Colors.gray20}` }}>
      <Flex align="center" style={{ gap: 8 }}>
        <Chip
          size="small"
          label={statusLabel(run, t)}
          sx={{
            backgroundColor: statusColor(run),
            color: Colors.white,
            textTransform: "capitalize",
            height: 22,
          }}
        />
        {run.result === "" && typeof run.progress === "number" && (
          <Comment>{run.progress}%</Comment>
        )}
        {onCancel && (
          <TextButton small disabled={cancelling} onClick={onCancel}>
            {t("cancel")}
          </TextButton>
        )}
        <Comment
          style={{ flex: 1, textAlign: "right" }}
          title={ts ? formatDateTime(ts, { seconds: true }) : ""}
        >
          {ts ? relativeTime(ts) || formatDateTime(ts, { seconds: true }) : ""}
        </Comment>
      </Flex>

      {run.result === "failed" && run.failure_reason && (
        <Text style={{ color: Colors.danger, display: "block", marginTop: 6 }}>
          {run.failure_reason}
        </Text>
      )}

      {/* The raw -/+ diff is admin-mode only. */}
      {adminMode && diff ? (
        <Box
          component="pre"
          sx={{
            mt: 1,
            mb: 0,
            p: 1,
            maxHeight: 160,
            overflow: "auto",
            backgroundColor: Colors.gray05,
            borderRadius: 1,
            fontSize: "0.75rem",
            whiteSpace: "pre-wrap",
            wordBreak: "break-word",
          }}
        >
          {diff}
        </Box>
      ) : !diff ? (
        <Comment style={{ display: "block", marginTop: 6 }}>
          {t("noChanges", { defaultValue: "No changes" })}
        </Comment>
      ) : null}
    </Box>
  );
}

export default function SyncHistory({ open, deviceId, onClose, adminMode }) {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [runs, setRuns] = useState([]);
  const [cancelling, setCancelling] = useState(false);
  // Already-cancelled run; hides the button while the list still lags behind.
  const [cancelledId, setCancelledId] = useState(null);

  // Last V4 websocket sync-run event; watching it refreshes the list live
  // instead of freezing at whatever it was when the dialog opened.
  const syncRunEvent = useSelector((s) => s[DEVICES].get("syncRunEvent"));

  // Latest event in a ref so the open effect can baseline it without re-running.
  const eventRef = useRef(syncRunEvent);
  eventRef.current = syncRunEvent;

  // Monotonic request token so the newest response always wins.
  const reqRef = useRef(0);
  // Highest event seq already reflected in `runs`; gates redundant refetches.
  const seenSeqRef = useRef(0);

  const fetchRuns = useCallback(
    (showSpinner) => {
      if (!deviceId) return;
      const reqId = ++reqRef.current;
      if (showSpinner) setLoading(true);
      setError(null);

      api
        .deviceConfigSyncsList(deviceId)
        .then((res) => {
          if (reqId !== reqRef.current) return;
          const list = Array.isArray(res?.data)
            ? res.data
            : res?.data?.data || [];
          const sorted = [...list]
            .sort(
              (a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0)
            )
            .slice(0, HISTORY_LIMIT);
          setRuns(sorted);
        })
        .catch((e) => {
          if (reqId === reqRef.current) {
            setError(e?.message || "Failed to load sync history");
          }
        })
        .finally(() => {
          if (reqId === reqRef.current) setLoading(false);
        });
    },
    [deviceId]
  );

  // The endpoint takes no run id, so it's only offered on the newest run.
  const cancelRun = useCallback(
    (runId) => {
      if (!deviceId) return;
      setCancelling(true);
      // A reopen or device swap bumps this; a late response must not refetch.
      const token = reqRef.current;

      api
        .deviceConfigSyncsCurrentRunCancelCreate(deviceId)
        .then(() => {
          if (token !== reqRef.current) return;
          setCancelledId(runId);
          // Cancelling ends the sync, but the saga that set isSynchronising
          // (if any) only clears it on the run's terminal websocket event.
          // Clear it now so the toolbar drops "Synchronising…" immediately.
          dispatch(updateDevice({ isSynchronising: false }));
          fetchRuns(false);
        })
        .catch((e) => {
          enqueueSnackbar(e?.message || t("somethingWentWrong"), {
            variant: "error",
          });
        })
        .finally(() => setCancelling(false));
    },
    [deviceId, fetchRuns, enqueueSnackbar, dispatch, t]
  );

  // Initial load on open / device change. Baseline the current seq so only newer
  // events trigger the background refresh below.
  useEffect(() => {
    if (!open || !deviceId) {
      reqRef.current++; // invalidate any in-flight request
      return undefined;
    }

    seenSeqRef.current = eventRef.current?.seq || 0;
    setRuns([]);
    setCancelling(false);
    setCancelledId(null);
    fetchRuns(true);
    return undefined;
  }, [open, deviceId, fetchRuns]);

  // Live refresh: re-fetch quietly on each newer sync-run event for this device
  // so the list and its progress % track the background toasts.
  useEffect(() => {
    if (!open || !deviceId || !syncRunEvent) return;
    if (syncRunEvent.deviceId !== deviceId) return;
    if ((syncRunEvent.seq || 0) <= seenSeqRef.current) return;

    seenSeqRef.current = syncRunEvent.seq;
    fetchRuns(false);
  }, [open, deviceId, syncRunEvent, fetchRuns]);

  return (
    <Dialog open={!!open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{t("history")}</DialogTitle>
      <DialogContent dividers>
        {loading ? (
          <Flex justify="center" style={{ padding: 24 }}>
            <CircularProgress size={28} />
          </Flex>
        ) : error ? (
          <Text style={{ color: Colors.danger }}>{error}</Text>
        ) : runs.length === 0 ? (
          <Comment>
            {t("noSyncHistoryYet", { defaultValue: "No sync history yet" })}
          </Comment>
        ) : (
          <>
            <Bold style={{ display: "block", marginBottom: 4 }}>
              {t("recentSyncRuns", { defaultValue: "Recent sync runs" })}
            </Bold>
            {runs.map((run, i) => (
              <SyncRunItem
                key={run.id}
                run={run}
                t={t}
                adminMode={adminMode}
                cancelling={cancelling}
                onCancel={
                  i === 0 && isCancellable(run) && run.id !== cancelledId
                    ? () => cancelRun(run.id)
                    : null
                }
              />
            ))}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <TextButton onClick={onClose}>{t("close")}</TextButton>
      </DialogActions>
    </Dialog>
  );
}
